import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import {
	SUBSCRIPTION_CANCEL,
	SUBSCRIPTION_STITCH_CANCEL,
	SUBSCRIPTION_REACTIVATION,
	SUBSCRIPTION_CHANGE,
	SUBSCRIPTION_CHOOSE,
	SUBSCRIPTION_TV_GEMIST_REACTIVATION,
	SUBSCRIPTION_TV_GEMIST_CANCEL,
	SUBSCRIPTION_UNTIL,
	FROM,
	CURRENT,
	NEW,
	SOON,
	STARTS_AT,
	SUBSCRIPTION_TV_GEMIST_NEXT_CANCEL,
	SUBSCRIPTION_TV_GEMIST_NEXT_SUBSCRIBE,
	TV_GEMIST_MODAL,
	TV_GEMIST_DESCRIPTION,
	MY_SUBSCRIPTION,
} from '../../../../constants/Copy';
import './TierDetails.scss';
import { getPresentationForTier, isOnFreeTier } from '../../../../helpers/tiers';
import { reactivateSubscription, cancelSubscription } from '../../../../actions/profileActions';
import { analyticsPush } from '../../../../actions/applicationActions';
import { cancellationStatuses } from '../../../../constants/GraphQL';
import { useDispatch, useSelector } from 'react-redux';
import {
	ROUTE_CHANGE_TIER_BEDROCK,
	ROUTE_OFFBOARDING,
	ROUTE_CANCEL_STITCH,
	ROUTE_OFFBOARDING_DETAILS,
} from '../../../../constants/Routes';
import { EVT_CHANGE_TIER_CLICK } from '../../../../constants/EventTypes';
import Button, {
	BUTTON_KIND_ACTION,
	BUTTON_KIND_WHITE,
	BUTTON_KIND_LABEL_ONLY,
} from '../../../common/buttons/button/Button';
import { TIER_SWITCH_TYPES } from '../../../../constants/TierSwitch';
import { useHistory } from 'react-router';
import { SwitchBlockedReasonMessage } from '../../switchBlockedReasonMessage';
import { CANCELLATION_BLOCKED_REASON } from '../../../../constants/CancelAccount';
import {
	getNextStatusEffectiveDate,
	getSubscriptionEndDate,
} from '../../../../helpers/subscription';
import { TIER_FREE, TIER_IDS } from '../../../../constants/Tiers';
import { getImageUrl } from '../../../../config';
import TvGemistModal from '../tvGemistModal/TvGemistModal';
import { useMutation } from '@apollo/client';
import mutationEnableTvGemistFallback from '../../../../graphql/mutationEnableTvGemistFallback.graphql';
import mutationDisableTvGemistFallback from '../../../../graphql/mutationDisableTvGemistFallback.graphql';
import { reportError } from '../../../../helpers/realUserMonitoring';
import { getSubscriptionClient } from '../../../../api/subscriptionService';
import Loader from '../../../common/loader/Loader';
import { friendlyDate } from '../../../../helpers/datetime';
import { ACTIVE_PLAN_PERIOD } from '../../../../constants/Plans';
import { formatPrice } from '../../../../helpers/number';
import { initiateTrackStructEvent } from '../../../analytics/Snowplow';
import {
	EVT_ACCOUNT,
	EVT_ELEMENT_INTERACTION,
	EVT_START,
	EVT_SUBSCRIPTION,
	EVT_SUBSCRIPTION_CHANGE,
	EVT_ELEMENT_TYPE_BUTTON,
	EVT_SUBSCRIPTION_CANCEL,
	EVT_ELEMENT_TYPE_HYPERLINK,
	EVT_CANCEL,
} from '../../../../constants/SnowplowEvents';
import { getExperiment } from '../../../../helpers/sitespect';
import { SITESPECT_EXPERIMENT_NAMES, SITESPECT_EXPERIMENT } from '../../../../constants/Sitespect';

const featureDescription = (features) =>
	features.map((feature) => feature.description_no_icon).join(', ');

const getPricing = (subscriptionPlan) => {
	let price;
	switch (subscriptionPlan.activePlanPeriod) {
		case ACTIVE_PLAN_PERIOD.DISCOUNTED:
			price = subscriptionPlan.planDescription?.discountedPeriod?.price;
			break;
		case ACTIVE_PLAN_PERIOD.PROMO:
			price =
				subscriptionPlan.planDescription.discountedPeriod?.price ||
				subscriptionPlan.planDescription.normalPeriod.price;
			break;
		default:
			price = subscriptionPlan.planDescription.normalPeriod.price;
	}
	return formatPrice(price);
};

const TierDetails = ({
	isCancelled,
	subscriptionGraphQlDetails,
	tierSwitch,
	nextTier,
	nextBillingDate,
	isTvGemistFallbackEnabled,
	onRefetchTvGemistFallback,
	isLoading,
	currentPlan,
}) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const activeSubscription = useSelector((state) => state.subscription.details.data);
	const stitchedSubscription = useSelector((state) => state.subscription.stitchedSubscription.data);
	const isCancellingBlocked =
		subscriptionGraphQlDetails?.details?.data?.allowsCancellation?.__typename ===
		cancellationStatuses.CANCELLATION_BLOCKED;
	const showCancelButton =
		!isCancelled &&
		subscriptionGraphQlDetails?.details?.data?.allowsCancellation?.reason !==
			CANCELLATION_BLOCKED_REASON.IS_FREE_SUBSCRIPTION;
	const isUserOnFreeTier = isOnFreeTier(currentPlan?.planDescription.tierId);
	const subscriptionClient = getSubscriptionClient();
	const [showTvGemistModal, setShowTvGemistModal] = useState(false);
	const [TvGemistModalTitle, setTvGemistModalTitle] = useState(TV_GEMIST_MODAL.TITLE);
	const subscriptionEndDate = getSubscriptionEndDate(
		activeSubscription,
		nextBillingDate,
		isCancelled
	);
	const isThirdParty = !!subscriptionGraphQlDetails?.details?.data?.trustedParty;
	const currentPlanId = currentPlan?.planDescription.planId;

	const prevIsCancelledRef = useRef(isCancelled);

	useEffect(() => {
		if (isCancelled && isCancelled !== prevIsCancelledRef.current) {
			onRefetchTvGemistFallback();
		}
		prevIsCancelledRef.current = isCancelled;
	}, [isCancelled, onRefetchTvGemistFallback]);

	function toggleTvGemistModal(show, title) {
		setTvGemistModalTitle(title);
		setShowTvGemistModal(show);
	}

	const isTierSwitchBlocked =
		isThirdParty || tierSwitch.__typename === TIER_SWITCH_TYPES.BLOCKED_TIER_SWTICH;
	const { subscriptionNumber } = activeSubscription;
	const tierPresentation = getPresentationForTier(currentPlan.planDescription.tierId);
	const stitchedTierPresentation = stitchedSubscription
		? getPresentationForTier(stitchedSubscription.tierId)
		: null;
	const nextTierPresentation = stitchedTierPresentation
		? stitchedTierPresentation
		: nextTier
		? getPresentationForTier(nextTier.tierId)
		: null;
	if (nextTierPresentation) {
		nextTierPresentation.price = stitchedSubscription
			? stitchedSubscription.price
			: nextTier
			? nextTier.price
			: undefined;
	}
	const testId = `tierdetails-container--${currentPlan.planDescription.tierId}`;

	const [enableTvGemistFallback] = useMutation(mutationEnableTvGemistFallback, {
		client: subscriptionClient,
	});
	const [disableTvGemistFallback] = useMutation(mutationDisableTvGemistFallback, {
		client: subscriptionClient,
	});

	const onChangeSubscriptionClick = async (analyticsPush) => {
		await initiateTrackStructEvent({
			category: EVT_ACCOUNT,
			action: EVT_SUBSCRIPTION_CHANGE,
			label: EVT_START,

			rtlElementContext: {
				elementInteraction: EVT_ELEMENT_INTERACTION,
				elementGroup: EVT_SUBSCRIPTION,
				elementType: EVT_ELEMENT_TYPE_BUTTON,
				elementName: EVT_SUBSCRIPTION_CHANGE,
				elementLabel: SUBSCRIPTION_CHANGE,
			},
			rtlFunnel: {
				funnelName: EVT_SUBSCRIPTION_CHANGE,
				funnelStepName: 'Start Subscription Change',
				funnelStep: '01',
				funnelSubscriptionType: currentPlanId,
			},
			...(subscriptionNumber && {
				rtlPurchase: {
					videolandSubscriptionNumber: subscriptionNumber,
				},
			}),
		});
		dispatch(analyticsPush({ label: SUBSCRIPTION_CHANGE }, EVT_CHANGE_TIER_CLICK));
		history.push(ROUTE_CHANGE_TIER_BEDROCK);
	};

	const onReactivateSubscription = () => {
		initiateTrackStructEvent({
			category: EVT_ACCOUNT,
			action: EVT_SUBSCRIPTION_CANCEL,
			label: EVT_CANCEL,

			rtlElementContext: {
				elementInteraction: EVT_ELEMENT_INTERACTION,
				elementGroup: EVT_SUBSCRIPTION,
				elementType: EVT_ELEMENT_TYPE_HYPERLINK,
				elementName: EVT_SUBSCRIPTION_CANCEL,
				elementLabel: isUserOnFreeTier
					? SUBSCRIPTION_TV_GEMIST_REACTIVATION
					: SUBSCRIPTION_REACTIVATION,
			},
		});

		dispatch(reactivateSubscription(subscriptionNumber));
	};

	const reactivateSubscriptionButton = () =>
		isCancelled ? (
			<div className="profile__subscription-details-button">
				<Button
					kind={BUTTON_KIND_LABEL_ONLY}
					data-test="reactivation-button"
					onClick={() => onReactivateSubscription()}
				>
					{isUserOnFreeTier ? SUBSCRIPTION_TV_GEMIST_REACTIVATION : SUBSCRIPTION_REACTIVATION}
				</Button>
			</div>
		) : null;

	const cancelFreeTierButton = (
		<div className="profile__subscription-details-button">
			<Button onClick={() => onChangeTvGemistFallback()}>
				{isTvGemistFallbackEnabled
					? SUBSCRIPTION_TV_GEMIST_NEXT_CANCEL
					: SUBSCRIPTION_TV_GEMIST_NEXT_SUBSCRIBE}
			</Button>
		</div>
	);

	const tierTitle = (endDate) => (
		<h2>
			{MY_SUBSCRIPTION}
			{isCancelled && (
				<span className="tierdetails__subtitle">
					{SUBSCRIPTION_UNTIL}
					{endDate ? ` ${endDate}` : '...'}
				</span>
			)}
		</h2>
	);

	const onEnableTvGemistFallback = () => {
		enableTvGemistFallback()
			.then(() => {
				onRefetchTvGemistFallback();
			})
			.catch((e) => {
				reportError(e);
			});
	};

	const onChangeTvGemistFallback = () => {
		if (isTvGemistFallbackEnabled) {
			disableTvGemistFallback()
				.then(() => {
					onRefetchTvGemistFallback();
				})
				.catch((e) => {
					reportError(e);
				});
		} else {
			onEnableTvGemistFallback();
		}
	};

	const handleOffboarding = () => {
		const isOffboardingDetailsExperiment = getExperiment(
			SITESPECT_EXPERIMENT_NAMES.OFFBOARDING_DETAILS
		);

		initiateTrackStructEvent({
			category: EVT_ACCOUNT,
			action: EVT_SUBSCRIPTION_CANCEL,
			label: EVT_START,

			rtlElementContext: {
				elementInteraction: EVT_ELEMENT_INTERACTION,
				elementGroup: EVT_SUBSCRIPTION,
				elementType: EVT_ELEMENT_TYPE_HYPERLINK,
				elementName: EVT_SUBSCRIPTION_CANCEL,
				elementLabel: isUserOnFreeTier ? SUBSCRIPTION_TV_GEMIST_CANCEL : SUBSCRIPTION_CANCEL,
			},
			rtlFunnel: {
				funnelName: EVT_SUBSCRIPTION_CANCEL,
				funnelStepName: 'Start Subscription Cancellation',
				funnelStep: '01',
				funnelSubscriptionType: currentPlanId,
			},
			...(subscriptionNumber && {
				rtlPurchase: {
					videolandSubscriptionNumber: subscriptionNumber,
				},
			}),
		});

		isUserOnFreeTier
			? dispatch(cancelSubscription())
			: isOffboardingDetailsExperiment === SITESPECT_EXPERIMENT.VARIANT
			? history.push(ROUTE_OFFBOARDING_DETAILS)
			: history.push(ROUTE_OFFBOARDING);
	};

	return isLoading ? (
		<div className="subscription-details">
			<div className="subscription-details__main subscription-details__main--loading">
				{tierTitle()}
				<Loader />
			</div>
		</div>
	) : (
		<>
			<TvGemistModal
				title={TvGemistModalTitle}
				description={
					isUserOnFreeTier ? TV_GEMIST_MODAL.DESCRIPTION_ACTIVE : TV_GEMIST_MODAL.DESCRIPTION
				}
				show={showTvGemistModal}
				onClose={() => {
					toggleTvGemistModal(false, TV_GEMIST_MODAL.TITLE);
					onRefetchTvGemistFallback();
				}}
			/>
			<div className="subscription-details">
				<div
					className={cx('subscription-details__main', {
						['subscription-details__main--loading']: isLoading,
					})}
				>
					{tierTitle(subscriptionEndDate)}
					<div className="tierdetails" data-test={testId}>
						<div
							className={cx('tierdetails__head', {
								['tierdetails__head-savod']:
									currentPlan.planDescription.tierId === TIER_IDS.PREMIUM_WITH_ADS ||
									currentPlan.planDescription.tierId === TIER_IDS.STANDARD_WITH_ADS,
							})}
						>
							<span className="tierdetails__tag" data-test="tierdetails-current">
								{CURRENT}
							</span>
							<h3 className="tierdetails__name" data-test="tierdetails-title">
								{tierPresentation.title}
							</h3>
						</div>

						<p className="tierdetails__features" data-test="tierdetails-features">
							{featureDescription(tierPresentation.features)}
						</p>

						{!isThirdParty && (
							<p className="tierdetails__price-period">
								<>
									<span className="tierdetails__price" data-test="tierdetails-price">
										&euro;
										{getPricing(currentPlan)}
									</span>
								</>
							</p>
						)}
					</div>
					{isTierSwitchBlocked && (
						<div className="subscription-cancelled-tooltip">
							<SwitchBlockedReasonMessage
								planId={currentPlanId}
								tierSwitch={tierSwitch}
								nextTier={nextTier}
								subscriptionGraphQlDetails={subscriptionGraphQlDetails}
								isThirdParty={isThirdParty}
							/>
						</div>
					)}
					<div className="profile__tier-details-change-tier">
						<Button
							disabled={isTierSwitchBlocked}
							kind={BUTTON_KIND_ACTION}
							onClick={() => onChangeSubscriptionClick(analyticsPush)}
							data-test="change-tier"
						>
							{isUserOnFreeTier ? SUBSCRIPTION_CHOOSE : SUBSCRIPTION_CHANGE}
						</Button>
						{showCancelButton && (
							<Button
								kind={BUTTON_KIND_WHITE}
								onClick={handleOffboarding}
								disabled={isCancellingBlocked}
							>
								{isUserOnFreeTier ? SUBSCRIPTION_TV_GEMIST_CANCEL : SUBSCRIPTION_CANCEL}
							</Button>
						)}
						{reactivateSubscriptionButton()}
					</div>
				</div>
				{isCancelled && !isUserOnFreeTier && (
					<section
						className={cx('subscription-details__free', {
							['subscription-details__free--disabled']: !isTvGemistFallbackEnabled,
						})}
						data-test="subscription-details-free"
					>
						{isLoading ? (
							<div>
								<Loader />
							</div>
						) : (
							<>
								<Button
									className="tierdetails-help"
									onClick={() => toggleTvGemistModal(true, TV_GEMIST_MODAL.TITLE_INFO)}
								>
									<img
										src={getImageUrl('/images/onboarding/help-icon-circled.svg')}
										alt="question icon"
									/>
								</Button>
								<div className="tierdetails">
									<span className="tierdetails__tag" data-test="tierdetails-current">
										{NEW}
									</span>
									<h2>
										{getPresentationForTier(TIER_IDS.FREE).title}
										<span className="tierdetails__subtitle">
											{FROM} {getNextStatusEffectiveDate(activeSubscription)}
										</span>
									</h2>

									<p className="tierdetails__features" data-test="tierdetails-features">
										{TV_GEMIST_DESCRIPTION}
									</p>

									<p className="tierdetails__price-period">
										<>
											<span className="tierdetails__price" data-test="tierdetails-price">
												&euro;
												{formatPrice(TIER_FREE.pricing.recurring)}
											</span>
										</>
									</p>
								</div>
								{cancelFreeTierButton}
							</>
						)}
					</section>
				)}
				{nextTierPresentation && (
					<section
						className={cx('subscription-details__stitched')}
						data-test="subscription-details-stitched"
					>
						{isLoading ? (
							<div>
								<Loader />
							</div>
						) : (
							<>
								<div className="tierdetails">
									<div className="tierdetails__title">
										<span className="tierdetails__tag" data-test="tierdetails-current">
											{SOON}
										</span>
									</div>
									<h2>
										{nextTierPresentation.title}
										{nextTierPresentation.startDate && (
											<span className="tierdetails__subtitle">
												{STARTS_AT} {friendlyDate(nextTierPresentation.startDate)}
											</span>
										)}
									</h2>

									<p className="tierdetails__features" data-test="tierdetails-features">
										{featureDescription(nextTierPresentation.features)}
									</p>
									{nextTierPresentation.price && (
										<p className="tierdetails__price-period">
											<span className="tierdetails__price" data-test="tierdetails-price">
												&euro;
												{formatPrice(nextTierPresentation.price)}
											</span>
										</p>
									)}
								</div>
								{stitchedSubscription && (
									<div className="profile__subscription-details-button">
										<Button href={ROUTE_CANCEL_STITCH}>{SUBSCRIPTION_STITCH_CANCEL}</Button>
									</div>
								)}
							</>
						)}
					</section>
				)}
			</div>
		</>
	);
};

export default TierDetails;
